import { Button } from '@/components/ui/button';
import { Copy, Check } from 'lucide-react';
import { useState } from 'react';
import { toast } from '@/hooks/use-toast';
import { JobWithRelations } from '@/types';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';
import { useJobBrandName } from '@/hooks/useJobBrandName';

interface CopyJobDetailsButtonProps {
  job: JobWithRelations;
}

export function CopyJobDetailsButton({ job }: CopyJobDetailsButtonProps) {
  const [copied, setCopied] = useState(false);
  const { formatShortDate } = useCompanyLocale();
  // The trading name the technician answers as. Resolved the same way jobs_tech
  // resolves it for the app, so the pasted message and the app never disagree.
  const brandName = useJobBrandName(job.lead_id);

  const KNOWN_LABELS = [
    'Job',
    'Company',
    'Customer Name',
    'Phone Number',
    'Address',
    'Issue',
    'ETA',
    'Note',
    'Notes',
  ];

  const extractFromDescription = (text: string | null | undefined, label: string): string => {
    if (!text) return '';
    const lines = text.split('\n');
    const matchesLabel = (line: string, l: string) => {
      const t = line.trim().toLowerCase();
      const lc = l.toLowerCase();
      return t.startsWith(`${lc}:`) || t.startsWith(`${lc} :`);
    };
    const startIdx = lines.findIndex(l => matchesLabel(l, label));
    if (startIdx === -1) return '';

    const others = KNOWN_LABELS.filter(l => l.toLowerCase() !== label.toLowerCase());
    const firstLine = lines[startIdx];
    const colonIdx = firstLine.indexOf(':');
    const collected = [firstLine.slice(colonIdx + 1).trim()];

    for (let i = startIdx + 1; i < lines.length; i++) {
      if (others.some(l => matchesLabel(lines[i], l))) break;
      collected.push(lines[i]);
    }

    return collected.join('\n').trim();
  };

  const normalise = (v: string | null | undefined): string =>
    (v ?? '').toLowerCase().replace(/\s+/g, ' ').trim();
  const digitsOf = (v: string | null | undefined): string => (v ?? '').replace(/\D/g, '');

  /**
   * Fallback for an issue field with no "Issue:" label: often a whole dispatch
   * message pasted in (job number, trading name, customer, phone, ETA, fault).
   * The template already prints the job number, name, phone, ETA and trading
   * name, so lines that only repeat those are dropped. Anything else (full
   * address, maps link, the fault itself) is kept word for word.
   */
  const stripDuplicateLines = (text: string | null | undefined): string => {
    if (!text) return '';
    const known = new Set(
      [job.job_number, job.customer_name, brandName].map(normalise).filter(Boolean),
    );
    const phone = digitsOf(job.customer_phone);
    // "Everserve - Melbourne" style line: brand plus a region, already covered by "Trading as".
    const brand = normalise(brandName);
    const kept = text.split('\n').filter(line => {
      const n = normalise(line);
      if (!n) return true;
      if (known.has(n)) return false;
      if (brand && n.startsWith(brand) && /^[\s:|–—-]/.test(n.slice(brand.length))) return false;
      if (/^eta\s*:/.test(n)) return false;
      if (phone && digitsOf(line) === phone && /^[+\d\s()-]+$/.test(line.trim())) return false;
      return true;
    });
    return kept.join('\n').replace(/^\n+|\n+$/g, '').replace(/\n{3,}/g, '\n\n');
  };

  const formatTime = (t: string | null | undefined): string => (t ? t.slice(0, 5) : '');

  const formatEta = (): string => {
    if (!job.scheduled_date) return 'TBD';
    const date = formatShortDate(job.scheduled_date);
    const start = formatTime(job.scheduled_time_start);
    const end = formatTime(job.scheduled_time_end);
    if (start && end) return `${date} between ${start} - ${end}`;
    if (job.scheduled_time) return `${date} at ${job.scheduled_time}`;
    return date;
  };

  // Street line plus suburb, state and postcode. A part already typed into the
  // street line (e.g. "8 James Ave Aspendale VIC 3195") is not repeated.
  const fullAddress = (): string => {
    const street = (job.customer_address ?? '').trim();
    const lowerStreet = street.toLowerCase();
    const extras = [job.customer_suburb, job.customer_state, job.customer_postcode]
      .map(p => (p ?? '').trim())
      .filter(p => p && !lowerStreet.includes(p.toLowerCase()));
    return [street, ...extras].filter(Boolean).join(', ');
  };

  const formatJobForWhatsApp = () => {
    const issue =
      extractFromDescription(job.issue_description, 'Issue') ||
      stripDuplicateLines(job.issue_description);
    const note = extractFromDescription(job.issue_description, 'Note');
    const service = job.service_types?.name || '';

    let result = `New Job: ${job.job_number}${brandName ? `
Trading as: ${brandName}` : ''}
Customer Name: ${job.customer_name}
Phone Number: ${job.customer_phone}
Address: ${fullAddress()}
Service: ${service}
Issue: ${issue}

ETA: ${formatEta()}`;

    if (note) {
      result += `\n\nNote: ${note}`;
    }

    return result;
  };

  const handleCopy = async () => {
    try {
      const text = formatJobForWhatsApp();
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast({
        title: 'Copied to clipboard!',
        description: 'Job details ready to send via WhatsApp',
      });
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast({
        title: 'Failed to copy',
        description: 'Please try again',
        variant: 'destructive',
      });
    }
  };

  return (
    <Button onClick={handleCopy} variant="outline" className="gap-2">
      {copied ? (
        <>
          <Check className="h-4 w-4" />
          Copied!
        </>
      ) : (
        <>
          <Copy className="h-4 w-4" />
          Copy for Tech
        </>
      )}
    </Button>
  );
}
